import { useQuery } from '@tanstack/react-query';
import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from 'react';
import { qk } from '../../lib';
import { getMe } from '../../features/auth';
import { useFetchQuickFilterDetails } from '../../mutations';

/* ================= TYPES ================= */

export type FilterItem = {
    value: string;
    label: string;
}

export type Filters = Record<FilterKey, FilterItem[]>;

export type FilterKey = 'programme' | 'region' | 'workstream' | 'worktype-group' | 'projectYear' | 'site' | 'stakeholder';
export type OptionType = 'programme' | 'region' | 'network' | 'site' | 'workstream' | 'stakeholder' | 'stage-gates' | 'county' | 'iln' | 'worktype' | 'worktype-group' | 'projectYear' | 'phases' | 'contractor' | 'coam-area' | 'business-roles' ;

export type FilterContextType = {
    options: Record<OptionType, any[]>;
    setOptions: React.Dispatch<React.SetStateAction<Record<OptionType, any[]>>>;
    loading: boolean;
    error: Record<OptionType, string | null>;
    quickFilters: Filters;
    tempFilters: Filters;
    handleUpdateTempFilters: (key: FilterKey, value: FilterItem[]) => void;
    handleUpdateMultipleTempFilters: (updates: Partial<Filters>) => void;
    handleApplyFilters: () => void;
    handleResetFilters: () => void;
};

/* ================= CONTEXT ================= */

const FilterContext = createContext<FilterContextType | undefined>(undefined);

/* ================= PROVIDER ================= */

export const FilterContextProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { data: session } = useQuery({
        queryKey: qk.me(),
        queryFn: getMe,
    });

    const masterDataService = useFetchQuickFilterDetails();

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState<Record<OptionType, string | null>>({
        'programme': null,
        'region': null,
        'site': null,
        'stage-gates': null,
        'county': null,
        'iln': null,
        'workstream': null,
        'worktype': null,
       'worktype-group': null,
        'projectYear': null,
        'stakeholder': null,
        'phases': null,
        'contractor': null,
        'coam-area': null,
        'network': null,
        'business-roles': null
    });

    const optionsList: OptionType[] = [
        'programme',
        'region',
        'stage-gates',
        'county',
        'iln',
        'workstream',
        'worktype',
        'worktype-group',
        'phases',
        'contractor',
        'coam-area',
        'network',
        'business-roles',
        
    ];

    const [options, setOptions] = useState<Record<OptionType, any[]>>({
        'programme': [],
        'region': [],
        'site': [],
        'stage-gates': [],
        'county': [],
        'iln': [],
        'workstream': [],
        'worktype': [],
        'worktype-group': [],
        'projectYear': [],
        'stakeholder': [],
        'phases': [],
        'contractor': [],
        'coam-area': [],
        'network': [],
        'business-roles': [],
        
    });

    const emptyFilters: Filters = {
        programme: [],
        region: [],
        workstream: [],
        'worktype-group': [],
        projectYear: [],
        site: [],
        stakeholder: [],
    }

    const [quickFilters, setQuickFilters] = useState<Filters>(emptyFilters);

    const [tempFilters, setTempFilters] = useState<Filters>(emptyFilters);

    /* ================= HANDLERS ================= */

    const handleUpdateTempFilters = (key: FilterKey, value: FilterItem[]) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleApplyFilters = () => {
        setQuickFilters({ ...tempFilters });
    };

    const handleResetFilters = () => {
        setTempFilters(emptyFilters);
        setQuickFilters(emptyFilters);
    };

    const handleUpdateMultipleTempFilters = (updates: Partial<Filters>) => {
        setTempFilters((prev) => ({
            ...prev,
            ...updates,
        }));
    };

    /* ================= LOAD OPTIONS ================= */

    const loadOptions = async () => {
        try {
            setLoading(true);

            setError(
                optionsList.reduce(
                    (acc, key) => ({ ...acc, [key]: null }),
                    {} as Record<OptionType, string | null>,
                ),
            );

            const results = await Promise.all(
                optionsList.map(async (key) => {
                    try {
                        const res = await masterDataService.mutateAsync(key);
                        return [key, res.data] as const;
                    } catch {
                        return [key, null] as const;
                    }
                }),
            );

            const newOptions = results.reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: value ?? [],
                }),
                {} as Record<OptionType, any[]>,
            );

            const newErrors = results.reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: value ? null : 'Failed to load',
                }),
                {} as Record<OptionType, string | null>,
            );

            setOptions(newOptions);
            setError(newErrors);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (session?.authenticated) {
            loadOptions();
        }
    }, [session?.authenticated]);

    /* ================= PROVIDER ================= */

    return (
        <FilterContext.Provider
            value={{
                options,
                setOptions,
                loading,
                error,
                quickFilters,
                tempFilters,
                handleUpdateTempFilters,
                handleUpdateMultipleTempFilters,
                handleApplyFilters,
                handleResetFilters,
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

/* ================= HOOK ================= */

export const useFilterContext = () => {
    const context = useContext(FilterContext);
    if (!context) {
        throw new Error(
            'useFilterContext must be used within a FilterContextProvider',
        );
    }
    return context;
};